/**
 * Head (title, description, keywords) and h1 of /ai-music-video-generator in
 * the languages the page is translated into (15: the 13 site languages
 * besides English, plus Thai and Traditional Chinese). Shared by the server
 * (server/_core/seo.ts buildMvGeneratorSeo) and the React page
 * (client/src/pages/DirectorHome.tsx, through
 * shared/sharedComponentRouteSeo.ts for the head and getMvGeneratorHead for
 * the hero h1), so hydration cannot swap the title or the h1. Small on
 * purpose: DirectorHome reads it at hydration. The body copy (intro, steps,
 * features, FAQ) is in shared/mvGeneratorLocales/{lang}.ts, a lazy chunk on
 * the client. The English head and server h1 stay where they were
 * (shared/sharedComponentRouteSeo.ts and ROUTE_SEO in server/_core/seo.ts)
 * and the English hero keeps the generator's own line; the English body is
 * shared/mvGeneratorLocales/en.ts like every other language's. A language
 * without an entry here serves the English copy.
 *
 * Why (Oct 13 2026): the English page ranks #1-2 for "ai music video
 * generator" in Japan, Korea and Brazil, but /ja, /ko and /pt served the
 * English title, h1 and body under a lang attribute, so searches in the
 * local language had nothing to match. Monthly Google searches (Ahrefs,
 * Oct 13), which each title and h1 leads with and the body uses:
 *  - Japan says "MV" more than "ミュージックビデオ": "ai mv作成" 250, "mv作成
 *    ai" 200, "mv ai生成" 150, "ai ミュージックビデオ" 150, "ミュージックビデオ
 *    ai" 150, "mv ai" 150, "ミュージックビデオ作成ai 無料" 250, "aiでmvを作る"
 *    100, "mv 自動生成ai" 140.
 *  - Korea: "ai 뮤직비디오 만들기" 250, "뮤직비디오 생성 ai" 250, "뮤직비디오
 *    ai" 200, "ai 뮤직비디오" 200, "무료 ai 뮤직비디오 만들기" 100, "뮤비
 *    만들어주는 ai" 90, "ai 뮤비" 70.
 *  - Brazil: "criar videoclipe com ia gratuito" 350, "criar clipe de música
 *    com ia gratuito" 250, "fazer clipe com ia" 150, "criar clipe musical com
 *    ia" 150, "criar videoclipe com ia" 100, "como fazer um clipe musical com
 *    ia" 80.
 *
 * "Free" is said only where the English page says it (free to start, the
 * free 480p preview, the CTA, the free-plan question), never as a free full
 * video. No "free" in a title: Kitty had it removed from the English title
 * (PR #306).
 *
 * The h1 has two parts because the hero draws the second in the spectrum
 * gradient. Each was measured in the hero (64px at 1440 wide, 36px at 390)
 * to take the same number of lines as the hero line it replaces (one line
 * on desktop and two on a phone for ja and ko, two and three for pt), so
 * the generator does not move. A longer h1 pushes it down.
 *
 * Thai (th) and Traditional Chinese (tw) are page languages
 * (shared/pageLanguages.ts): they exist on this page only, so their entries
 * are the whole of /th/ and /tw/. Monthly Google searches (Ahrefs, Oct 13):
 *  - Thailand says "MV" with สร้าง or ทำ: "สร้าง mv ด้วย ai ฟรี" 150, "สร้าง mv
 *    เพลงด้วย ai ฟรี" 100, "สร้าง mv ด้วย ai" 60, "mv ai" 50, "ทำ mv ด้วย ai"
 *    40, "สร้างมิวสิควิดีโอด้วย ai" 40, "ai mv" 40. The long word is spelled
 *    มิวสิควิดีโอ, as it is searched.
 *  - Taiwan: "ai mv" 150, "ai 生成 mv 免費" 100, "mv製作" 80, "ai mv生成" 60,
 *    "音樂mv製作" 50, "免費mv製作軟體" 50, "ai mv製作" 50, "ai做mv" 40,
 *    "ai生成mv" 40, "音樂生成影片ai" 30. 製作 and 生成 are both used.
 * Their h1s take one line on desktop and two on a phone, as the English hero
 * does. Thai has no spaces between words, so a browser breaks it wherever
 * its dictionary finds a word; client/src/pages/creative-tools.css keeps the
 * gradient half in one piece on these two pages, so the line breaks between
 * the halves. Keep a Thai h1 under about 27 visible characters: Thai fonts
 * differ in width from one system to the next.
 *
 * To add a language: one entry in MV_GENERATOR_HEADS below, one file in
 * shared/mvGeneratorLocales with its line in shared/mvGeneratorBody.ts, its
 * lyric video and Canvas sentences in the page's entry in
 * shared/toolSignposts.ts, and one row in the EXPECTED table of
 * server/_core/seoMvGeneratorI18n.test.ts. The server builder, the React
 * page and the test suites pick the language up from here and need no
 * change. Measure its h1 in the hero first.
 */
export const MV_GENERATOR_PATH = "/ai-music-video-generator";

export type MvGeneratorHead = {
  /** <title>. At most 60 characters (ja, ko, zh and tw: 32). */
  title: string;
  /** Meta description. At most 160 characters (ja, ko, zh and tw: 80). */
  description: string;
  keywords: string[];
  /** [plain part, gradient part]; mvGeneratorH1 joins them with a space. */
  h1: readonly [string, string];
};

/**
 * Body copy of one language (shared/mvGeneratorLocales/{lang}.ts). English
 * is en.ts; a translation carries the same facts, and the same number of
 * steps, features and questions in the same order (asserted in
 * server/_core/seoMvGeneratorI18n.test.ts).
 */
export type MvGeneratorBody = {
  intro: string;
  /** The crawler HTML's link to the generator (?start=1). */
  ctaLabel: string;
  /** Breadcrumb names: the homepage, then this page. */
  breadcrumbs: { home: string; page: string };
  howTo: {
    /** The visible h2 over the steps. */
    heading: string;
    /** HowTo schema name and description (not shown on the page). */
    name: string;
    description: string;
    steps: Array<{ name: string; text: string }>;
  };
  featuresHeading: string;
  features: string[];
  faqHeading: string;
  faq: Array<{ q: string; a: string }>;
};

const MV_GENERATOR_HEADS: Record<string, MvGeneratorHead> = {
  ja: {
    title: "AI MV作成｜曲からミュージックビデオを自動生成｜Solmi",
    description:
      "AI MV作成のSolmiが、どんな曲もSunoの曲もMP3も、リップシンク付きのミュージックビデオに仕上げます。ビートに合う映像、歌詞の自動検出。無料で開始。",
    keywords: [
      "ai mv作成",
      "mv作成 ai",
      "mv ai生成",
      "ai ミュージックビデオ",
      "ミュージックビデオ ai",
      "ミュージックビデオ作成ai 無料",
      "aiでmvを作る",
      "mv 自動生成ai",
      "ai music video generator",
      "solmi",
    ],
    h1: ["AI MV作成", "曲をミュージックビデオに"],
  },
  ko: {
    title: "AI 뮤직비디오 만들기 | 노래로 뮤비 자동 생성",
    description:
      "노래, Suno 곡, MP3를 올리면 립싱크까지 맞춘 완성된 뮤직비디오를 AI가 생성합니다. 비트에 맞춘 영상, 가사 자동 인식. 무료로 시작.",
    keywords: [
      "ai 뮤직비디오 만들기",
      "뮤직비디오 생성 ai",
      "뮤직비디오 ai",
      "ai 뮤직비디오",
      "무료 ai 뮤직비디오 만들기",
      "뮤비 만들어주는 ai",
      "ai 뮤비 만들기",
      "ai 뮤직비디오 제작",
      "ai music video generator",
      "solmi",
    ],
    h1: ["AI 뮤직비디오 만들기 —", "어떤 노래든 뮤비로"],
  },
  pt: {
    title: "Criar videoclipe com IA: gerador de clipes de música | Solmi",
    description:
      "Gerador de videoclipes com IA: qualquer música, faixa do Suno ou MP3 vira clipe pronto com lip-sync, visuais no ritmo e letra automática. Grátis para começar.",
    keywords: [
      "criar videoclipe com ia",
      "criar videoclipe com ia gratuito",
      "criar clipe de música com ia",
      "fazer clipe com ia",
      "criar clipe musical com ia",
      "videoclipe com ia",
      "gerador de videoclipes com ia",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Criar videoclipe com IA:", "qualquer música vira clipe."],
  },
  // Germany (3,130 searches a month in the top 16 terms): "musikvideo mit ki
  // erstellen" 700, "ki musikvideo erstellen kostenlos" 600, "ki musikvideo
  // erstellen" 450, "ki musikvideo generator" 200, "ki musik video erstellen"
  // 200. "KI" leads; "AI Musikvideo" is the minority spelling /de/ already
  // ranks for, so the body names it once. Hyphenated as German spells it
  // (Google reads "KI-Musikvideo" as both words). The title carries
  // "Generator"; the description and the how-to heading open with
  // "Musikvideo mit KI erstellen", and the description says "kostenlos"
  // early, where a phone still shows it. Hero: two lines at 1440 wide and
  // three at 390, as "Verwandle jeden Song in ein Musikvideo." took.
  de: {
    title: "KI-Musikvideo erstellen: der Generator für jeden Song",
    description:
      "Musikvideo mit KI erstellen – kostenlos starten. Aus jedem Song, Suno-Track oder MP3 wird ein fertiges Musikvideo mit Lip-Sync, Visuals im Takt und Lyrics.",
    keywords: [
      "ki musikvideo erstellen",
      "musikvideo mit ki erstellen",
      "ki musikvideo erstellen kostenlos",
      "ki musikvideo generator",
      "ki musik video erstellen",
      "musikvideo erstellen ki",
      "ki musikvideo",
      "ai musikvideo erstellen",
      "ai musikvideo generator",
      "ai music video generator",
      "solmi",
    ],
    h1: ["KI-Musikvideo erstellen:", "aus jedem Song ein Video."],
  },
  // France (1,920): "clip ia" 350 (ambiguous: short-clip makers rank too),
  // "ia clip musique" 300, "clip ia gratuit" 250, "ia clip video" 150, "clip
  // musical ia gratuit" 150, "faire un clip avec une ia gratuit" 100, "creer un
  // clip video avec ia" 70. France says "clip", never "vidéo musicale". The
  // lead is the unambiguous music phrase; "musique" sits beside "clip" in
  // the h1. Hero: two lines at 1440 and three at 390, as "Transformez chaque
  // chanson en clip vidéo." took.
  fr: {
    title: "Clip musical IA : créer un clip vidéo avec l'IA | Solmi",
    description:
      "Clip musical IA : votre chanson, titre Suno ou MP3 devient un clip vidéo avec lip-sync, visuels calés sur le rythme et paroles auto. Gratuit pour commencer.",
    keywords: [
      "clip musical ia",
      "clip ia",
      "ia clip musique",
      "clip vidéo ia",
      "créer un clip vidéo avec ia",
      "créer un clip musical avec ia",
      "faire un clip avec une ia",
      "clip musique ia",
      "générateur de clip musique ia",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Clip musical IA :", "transformez votre musique en clip."],
  },
  // Indonesia (about 1,570 with music video intent): "membuat video klip ai
  // gratis" 200, "cara membuat video klip lagu dengan ai" 150, "buat video
  // klip ai gratis" 150, "video klip ai" 150, "buat video musik ai gratis"
  // 150, "buat video klip ai" 100. Always "video klip": "ai klip video" (350)
  // is a search for tools that cut long videos into short clips. Sentence
  // case, not the Title Case of the other Indonesian heads: the hero line it
  // replaces, "Ubah lagu apa pun menjadi video musik.", takes two lines at
  // 1440 and two at 390, and Title Case is too wide for two on a phone. The
  // description says "video musik" with no "utuh": for a video that word
  // means full-length, which the free tier is not.
  id: {
    title: "Buat video klip AI: ubah lagu jadi video musik | Solmi",
    description:
      "Buat video klip AI di Solmi: lagu apa pun, lagu Suno, atau MP3 jadi video musik, lengkap dengan lip-sync, visual sesuai beat, dan lirik otomatis. Mulai gratis.",
    keywords: [
      "buat video klip ai",
      "membuat video klip ai",
      "membuat video klip dengan ai",
      "cara membuat video klip lagu dengan ai",
      "video klip ai",
      "buat video musik ai",
      "bikin video klip ai",
      "generator video musik ai",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Buat video klip AI:", "lagu jadi video musik."],
  },
  // Italy (1,120): "creare video musicali con ai gratis" 350, "crea video
  // musicale con ai gratis" 150, "creare video musicali con ai" 90, "crea
  // video musicale con ai" 70, "crea videoclip con ai" 50. Searchers write
  // "AI", not "IA", so this page does too (the rest of the Italian site says
  // "IA"). Hero: two lines at 1440 and three at 390, as "Trasforma ogni
  // canzone in un videoclip." took.
  it: {
    title: "Creare video musicali con AI: un videoclip da ogni canzone",
    description:
      "Crea video musicali con AI: ogni canzone, brano Suno o MP3 diventa un videoclip finito con lip-sync, immagini a tempo e testo automatico. Gratis per iniziare.",
    keywords: [
      "creare video musicali con ai",
      "crea video musicale con ai",
      "creare video musicali con ai gratis",
      "video musicali ai",
      "video musicali con ai",
      "crea videoclip con ai",
      "creare videoclip con ai",
      "videoclip ai",
      "generatore video musicali ai",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Creare video musicali con AI:", "dalla canzone al videoclip."],
  },
  // Spain (950) and Mexico (600) on one page. Spain says "videoclip": "crear
  // videoclip con ia gratis" 200, "crear videoclip con ia" 70, "videoclip ia"
  // 70, "hacer videoclip musical con ia gratis" 60. Mexico says "video
  // musical": "crear videos musicales con ia gratis" 100 in each country,
  // "crear video musical con ia gratis" 70 in each, "crear video musical con
  // ia" 60. The title and the h1 carry both, and the description opens with
  // Mexico's verb phrase where the title opens with Spain's. Hero: two lines
  // at 1440 and three at 390, as "Convierte cualquier canción en un video
  // musical." took.
  es: {
    title: "Crear videoclip con IA: generador de videos musicales",
    description:
      "Crea videos musicales con IA: convierte cualquier canción, tema de Suno o MP3 en videoclip con lip-sync, imágenes al ritmo y letra automática. Empieza gratis.",
    keywords: [
      "crear videoclip con ia",
      "crear videos musicales con ia",
      "crear video musical con ia",
      "crear videoclip con ia gratis",
      "videoclip con ia",
      "videoclip ia",
      "hacer videoclip con ia",
      "videos musicales con ia",
      "generador de videos musicales con ia",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Crear videoclip con IA:", "de canción a video musical."],
  },
  // Turkey (920): "yapay zeka ile klip yapma" 200, "yapay zeka klip yapma
  // ücretsiz" 80, "şarkıya klip yapan yapay zeka" 80, "yapay zeka klip" 70,
  // "yapay zeka ile şarkıya klip yapma" 70, "yapay zeka klip oluşturucu" 50.
  // "Klip" with "yapay zeka"; "müzik videosu", the generator's own name for
  // it, is in the h1. The h1 addresses no one: the hero subtitle under it
  // says "siz" and the page body "sen". Hero: two lines at 1440 and three at
  // 390, as "Her şarkıyı müzik videosuna dönüştürün." took.
  tr: {
    title: "Yapay Zeka ile Klip Yapma: Her Şarkıya Klip Oluştur | Solmi",
    description:
      "Yapay zeka ile klip yapma: Solmi her şarkıyı, Suno parçasını veya MP3'ü dudak senkronlu, ritme uyan bir klibe çevirir, sözleri otomatik ekler. Ücretsiz başla.",
    keywords: [
      "yapay zeka ile klip yapma",
      "şarkıya klip yapan yapay zeka",
      "yapay zeka klip",
      "yapay zeka ile şarkıya klip yapma",
      "klip yapan yapay zeka",
      "yapay zeka klip oluşturucu",
      "yapay zeka video klip oluşturma",
      "yapay zeka ile klip nasıl yapılır",
      "yapay zeka müzik videosu",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Yapay Zeka ile Klip Yapma:", "Her Şarkıya Bir Müzik Videosu"],
  },
  // Poland (760): "teledysk ai" 150 (the /pl/ homepage is #6 for it),
  // "tworzenie teledysku ai za darmo" 70, "teledysk ai do muzyki" 60,
  // "tworzenie teledysku do piosenki ai" 50, "jak zrobić teledysk ai" 50,
  // "ai teledysk do piosenki" 50, "generator teledysku ai" 40. Hero: one line
  // at 1440 and two at 390, as "Zamień każdą piosenkę w teledysk." took, so
  // the h1 is short.
  pl: {
    title: "Teledysk AI: tworzenie teledysku do każdej piosenki | Solmi",
    description:
      "Generator teledysku AI: Solmi zamienia każdą piosenkę, utwór z Suno lub MP3 w gotowy teledysk z lip-syncem, obrazem w rytm muzyki i tekstem. Zacznij za darmo.",
    keywords: [
      "teledysk ai",
      "tworzenie teledysku ai",
      "teledysk ai do muzyki",
      "tworzenie teledysku do piosenki ai",
      "jak zrobić teledysk ai",
      "ai teledysk do piosenki",
      "generator teledysku ai",
      "teledysk do piosenki ai",
      "jak stworzyć teledysk ai",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Teledysk AI", "do każdej piosenki."],
  },
  // Vietnam (440, small and noisy: "mv ai" also reads "whose MV"): "tạo mv ca
  // nhạc bằng ai" 40, "tạo video âm nhạc bằng ai" 30, "tạo video ca nhạc
  // bằng ai" 30, "tạo video nhạc bằng ai" 20, "tạo mv bằng ai" 20. "Tạo …
  // bằng AI" throughout, "MV" first. Hero: one line at 1440 and two at 390,
  // as "Biến mọi bài hát thành video âm nhạc." took, so the h1 is short.
  vi: {
    title: "Tạo MV ca nhạc bằng AI: video âm nhạc từ mọi bài hát | Solmi",
    description:
      "Tạo MV ca nhạc bằng AI: Solmi biến mọi bài hát, bài Suno hay MP3 thành video âm nhạc hoàn chỉnh có lip-sync, hình ảnh khớp nhịp, lời tự động. Bắt đầu miễn phí.",
    keywords: [
      "tạo mv ca nhạc bằng ai",
      "tạo video âm nhạc bằng ai",
      "tạo video ca nhạc bằng ai",
      "tạo video nhạc bằng ai",
      "tạo mv bằng ai",
      "mv ai",
      "làm video nhạc bằng ai",
      "cách tạo video ca nhạc bằng ai",
      "trình tạo video nhạc ai",
      "ai music video generator",
      "solmi",
    ],
    h1: ["Tạo MV ca nhạc bằng AI", "từ bài hát."],
  },
  // Netherlands (170): "gratis ai videoclip maker" 30, "ai videoclip
  // generator" 30, "videoclip maken met ai" 20, "ai videoclip maker" 20,
  // "muziekvideo maken met ai" 10. Spelled as searched, without hyphens, like
  // the other Dutch tool names on the site. Hero: two lines at 1440 and three
  // at 390, as "Maak van elk nummer een muziekvideo." took.
  nl: {
    title: "AI videoclip maker: muziekvideo maken met AI | Solmi",
    description:
      "AI videoclip generator van Solmi: maak van elk nummer, Suno-track of MP3 een complete muziekvideo met lip-sync, songtekst en beelden op de beat. Gratis starten.",
    keywords: [
      "ai videoclip maker",
      "ai videoclip generator",
      "videoclip maken met ai",
      "muziekvideo maken met ai",
      "gratis ai videoclip maker",
      "ai videoclip",
      "muziek videoclip maken met ai",
      "ai videoclip maken",
      "ai muziekvideo generator",
      "ai music video generator",
      "solmi",
    ],
    h1: ["AI videoclip maker:", "van elk nummer een muziekvideo."],
  },
  // Simplified Chinese: no volume data (Google is blocked in mainland China;
  // the page serves Singapore, Malaysia and overseas readers). Written around
  // "AI MV 制作", "AI 音乐视频生成器" and "AI 生成 MV", with "MV" kept
  // prominent. Hero: one line at 1440 and two at 390, as "把任何一首歌变成
  // 音乐视频。" took; on a phone it breaks after "任何歌曲".
  zh: {
    title: "AI MV 制作：上传歌曲，AI 生成 MV｜音乐视频生成器",
    description:
      "Solmi 的 AI MV 制作工具，把任何歌曲、Suno 作品或 MP3 做成带对口型的完整音乐视频。画面卡点，自动识别歌词，免费开始。",
    keywords: [
      "ai mv 制作",
      "ai 音乐视频生成器",
      "ai 生成 mv",
      "ai mv 生成",
      "ai 制作 mv",
      "mv 制作",
      "ai 音乐视频",
      "音乐视频制作",
      "ai music video generator",
      "solmi",
    ],
    h1: ["AI MV 制作", "任何歌曲都能生成 MV"],
  },
  // Thai, a page language. Title 53 characters. "ฟรี" (free) is in the
  // description, never in the title or the h1.
  th: {
    title: "สร้าง MV ด้วย AI: เปลี่ยนเพลงเป็นมิวสิควิดีโอ | Solmi",
    description:
      "ทำ MV เพลงด้วย AI กับ Solmi เปลี่ยนเพลงไหนก็ได้ ทั้งเพลงจาก Suno และไฟล์ MP3 เป็นมิวสิควิดีโอลิปซิงก์พร้อมใช้ ภาพเข้าจังหวะ ขึ้นเนื้อเพลงอัตโนมัติ เริ่มใช้ฟรี",
    keywords: [
      "สร้าง mv ด้วย ai",
      "สร้าง mv เพลงด้วย ai",
      "ทำ mv ด้วย ai",
      "สร้างมิวสิควิดีโอด้วย ai",
      "ai ทำ mv",
      "ai สร้าง mv จากเพลง",
      "mv ai",
      "ai mv",
      "ai music video generator",
      "solmi",
    ],
    h1: ["สร้าง MV ด้วย AI", "จากเพลงไหนก็ได้"],
  },
  // Traditional Chinese (Taiwan usage), a page language under /tw/. Title
  // 28 characters. "免費" (free) is in the description, never in the title
  // or the h1.
  tw: {
    title: "AI MV 製作｜任何歌曲都能生成音樂 MV｜Solmi",
    description:
      "Solmi 的 AI MV 生成工具，把任何歌曲、Suno 歌曲或 MP3 製作成對嘴同步的完整音樂影片。畫面對上節拍、自動上歌詞，免費開始使用。",
    keywords: [
      "ai mv",
      "ai mv 製作",
      "ai mv 生成",
      "ai 生成 mv",
      "mv 製作",
      "音樂 mv 製作",
      "ai 做 mv",
      "音樂生成影片 ai",
      "ai music video generator",
      "solmi",
    ],
    h1: ["AI MV 製作", "把歌曲變成音樂影片"],
  },
};

/** The languages with their own /{lang}/ai-music-video-generator copy. */
export const MV_GENERATOR_LANGUAGES: readonly string[] =
  Object.keys(MV_GENERATOR_HEADS);

/**
 * The localized head for /{lang}/ai-music-video-generator, or null for
 * English and the languages without one (the caller keeps the English head).
 */
export function getMvGeneratorHead(lang?: string): MvGeneratorHead | null {
  if (!lang || lang === "en") return null;
  return MV_GENERATOR_HEADS[lang] ?? null;
}

/** The h1 as one string: what the crawler HTML prints and the hero reads as. */
export function mvGeneratorH1(head: MvGeneratorHead): string {
  return `${head.h1[0]} ${head.h1[1]}`;
}
